<script lang="ts" setup>
// Vue core imports
  import { computed, ref } from 'vue'
  import { useTheme } from 'vuetify'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'

  // Component imports
  import LogoutDialog from '@/components/layouts/LogoutDialog.vue'
  // SVG icon imports
  import ChatIcon from '@assets/icons/chat.svg'
  import Logo from '@assets/logo/logo.svg'
  import LightLogo from '@assets/logo/light-logo.svg'
  import ProLogoLight from '@assets/logo/sina-pro-light.svg?url'
  import ProLogoDark from '@assets/logo/sina-pro-dark.svg?url'
  import MenuIcon from '@assets/icons/sidebar/menu.svg'
  import { useDisplay } from 'vuetify/framework'
  import { useTrackStore } from '@stores/track.ts'
  import { useAuthDialog } from '@/composables/useAuthDialog.ts'
  import { storeToRefs } from 'pinia'
  import router from '@/router'
  import NewSessionConfirmDialog from '@/components/dialogs/NewSessionConfirmDialog.vue'

  const { mobile } = useDisplay()
  const theme = useTheme()
  const drawer = useTemplateRef('drawer')
  const paymentDialog = usePaymentDialog()
  const authDialog = useAuthDialog()

  const authStore = useAuthStore()
  const consultationStore = useConsultationsStore()
  const { pendingRecommendation } = storeToRefs(consultationStore)

  const { t } = useI18n()

  const route = useRoute()


  const showLogout = ref<boolean>(false)

  const iconStyles = computed(() => ({
    '--icon-stroke': 'rgb(var(--v-theme-grey-300))',
    '--icon-fill': 'none',
  }))
  const showNewSessionConfirm = ref(false)

  computed(() => (route.params as { id: string | undefined })?.id)

  const startNewChat = (consultationPrompt: boolean) => {
    useTrackStore().track('start_new_session_clicked')
    if (!authStore?.isAuthenticated && !consultationPrompt) {
      showNewSessionConfirm.value = true
      return
    }
    useTrackStore().track('new_session_started')

    router.push('/')
  }

  const showDrawer = () => {
    if (drawer.value) drawer.value.showDrawer()
  }
</script>

<template>
  <!-- Sidebar -->
  <new-app-navigation-drawer
    ref="drawer"
    :mobile="mobile"
    @show-logout="showLogout = true"
    @start-new-chat="startNewChat"
  />

  <!-- Header -->
  <v-app-bar
    class="header-bar bg-background"
    :extension-height="mobile? 64 : 0"
    flat
  >
    <div
      class="header-content d-flex justify-between align-center ga-1 w-100 px-4 px-lg-6"
    >
      <v-btn
        v-if="mobile && authStore.isAuthenticated"
        density="compact"
        flat
        icon
        size="36"
        @click.stop="showDrawer"
      >
        <v-badge color="error" dot location="top end" :model-value="!!pendingRecommendation">
          <v-icon
            :icon="MenuIcon"
            size="36"
          />
        </v-badge>
      </v-btn>
      <v-btn
        v-if="mobile && !authStore.isAuthenticated"
        class="me-10"
        density="compact"
        flat
        icon
        size="36"
        @click.stop="showDrawer"
      >
        <v-badge color="error" dot location="top end" :model-value="!!pendingRecommendation">
          <v-icon
            :icon="MenuIcon"
            size="36"
          />
        </v-badge>
      </v-btn>
      <div
        v-else-if="!mobile && !authStore.isAuthenticated"
        style="width: 75px; height: 100%"
      />
      <router-link
        v-else
        class="logo-link"
        :to="{ path: '/' }"
      >
        <div v-if="authStore.isAuthenticated && !mobile" class="ps-6">
          <logo v-if="theme.global.name.value === 'dark'" class="flex-shrink-0" />
          <light-logo v-else class="flex-shrink-0" />
        </div>
      </router-link>
      <v-spacer />
      <v-btn
        v-if="! authStore.isSubscribed"
        v-track="{event:'sina_pro_clicked', eventData: {source: 'header'}}"
        class="bg-alpha-primary-3 rounded-pill"
        flat
        @click="authStore.isAuthenticated ? paymentDialog.show() : authDialog.show()"
      >
        {{ t('sina_pro') }}
      </v-btn>
      <v-img
        v-else-if="authStore.isAuthenticated && authStore.isSubscribed"
        height="16"
        :src="theme.global.name.value === 'dark' ? ProLogoDark : ProLogoLight"
        width="66"
      />
      <v-spacer />
      <language-toggle v-if="!authStore.isAuthenticated" />
      <v-btn
        density="compact"
        flat
        icon
        size="large"
        @click.stop="startNewChat(false)"
      >
        <v-icon
          :icon="ChatIcon"
          :style="iconStyles"
        />
        <v-tooltip
          activator="parent"
          content-class="bg-grey-900 text-caption font-weight-bold rounded-pill"
          location="bottom"
        >
          {{ t('new_session') }}
        </v-tooltip>
      </v-btn>
    </div>
  </v-app-bar>

  <!-- Logout Dialog -->
  <logout-dialog v-model="showLogout" />
  <settings-dialog />
  <new-session-confirm-dialog v-model="showNewSessionConfirm" />
</template>

<style lang="scss" scoped>
@use '@/styles/settings' as v;
@use 'sass:map';

.header-bar {
  :deep(.v-toolbar__content) {
    display: flex;
    flex-direction: column;

    @media (min-width: 959px) {
    }
  }

  :deep(.v-btn__content) {
    width: 100%;
  }

  .header-content {
    height: 64px;
  }

  :deep(.v-toolbar__extension) {
    order: -1;
  }
}

::v-deep(.v-list-item__prepend) {
  width: 36px;
}
</style>
